<template>
  <div>
    <template v-if="isKyc">
      <p class="has-text-weight-bold is-size-4 mb-1">
        {{ $t("mp:application.waiting_room.kyc.title") }}
      </p>
      <p class="mb-5">{{ $t("mp:application.waiting_room.kyc.subtitle") }}</p>

      <qrcode-vue
        v-if="mobileKyc && !isReviewed"
        :value="url"
        :size="200"
        level="M"
        class="qrcode mb-2"
      ></qrcode-vue>

      <a v-if="!isReviewed" :href="url" target="_blank">{{
        $t("mp:application.waiting_room.kyc.button.start")
      }}</a>
    </template>

    <template v-else>
      <p class="has-text-weight-bold is-size-4 mb-1">
        {{ $t("mp:application.waiting_room.sign.title") }}
      </p>
      <p class="mb-5">{{ $t("mp:application.waiting_room.sign.subtitle") }}</p>

      <qrcode-vue
        v-if="mobileSign && !isReviewed"
        :value="url"
        :size="200"
        level="M"
        class="qrcode mb-2"
      ></qrcode-vue>

      <a v-if="!isReviewed" :href="url" target="_blank">{{
        $t("mp:application.waiting_room.sign.button.start")
      }}</a>
    </template>

    <!--Statuses-->
    <div class="my-6" v-if="!isFailed">
      <div class="is-flex is-align-items-center mb-4">
        <b-icon
          icon-pack="fa"
          :icon="isStarted ? 'check-circle' : 'hourglass-half'"
          :type="isStarted ? 'is-green' : 'is-warning'"
          class="mr-2"
        ></b-icon>

        <span
          class="has-text-weight-medium"
          v-bind:class="[isStarted ? 'has-text-green' : 'hast-text-warning']"
          >{{ $t("mp:application.waiting_room.label.started") }}</span
        >
      </div>
      <div v-if="isRetry" class="is-flex is-align-items-center mb-4">
        <b-icon
          icon-pack="fa"
          icon="exclamation-triangle"
          type="is-warning"
          class="mr-2"
        ></b-icon>

        <span class="has-text-weight-medium has-text-warning">{{
          $t("mp:application.waiting_room.label.retry")
        }}</span>
      </div>
      <div class="is-flex is-align-items-center mb-4">
        <b-icon
          icon-pack="fa"
          :icon="isDone ? 'check-circle' : 'hourglass-half'"
          :type="isDone ? 'is-green' : 'is-warning'"
          class="mr-2"
        ></b-icon>

        <span
          class="has-text-weight-medium"
          v-bind:class="[isDone ? 'has-text-green' : 'hast-text-warning']"
          >{{ $t("mp:application.waiting_room.label.done") }}</span
        >
      </div>
      <div class="is-flex is-align-items-center mb-4">
        <b-icon
          icon-pack="fa"
          :icon="isReviewed ? 'check-circle' : 'hourglass-half'"
          :type="isReviewed ? 'is-green' : 'is-warning'"
          class="mr-2"
        ></b-icon>

        <span
          class="has-text-weight-medium"
          v-bind:class="[isReviewed ? 'has-text-green' : 'hast-text-warning']"
          >{{ $t("mp:application.waiting_room.label.review_success") }}</span
        >
      </div>
      <div class="is-flex is-align-items-center mb-4">
        <b-icon
          icon-pack="fa"
          :icon="isReviewed ? 'check-circle' : 'hourglass-half'"
          :type="isReviewed ? 'is-green' : 'is-warning'"
          class="mr-2"
        ></b-icon>

        <span
          class="has-text-weight-medium"
          v-bind:class="[isReviewed ? 'has-text-green' : 'hast-text-warning']"
          >{{
            $t("mp:application.waiting_room.label.review_in_progress")
          }}</span
        >
      </div>
      <br />
      <br />
      <b-button
        v-if="isReviewed"
        class="next-btn"
        type="is-blue"
        @click="gotoNext"
        >{{ $t("mp:application.waiting_room.button.next") }}</b-button
      >
    </div>
    <div class="my-6" v-if="isFailed">
      <div class="is-flex is-align-items-center mb-4">
        <b-icon
          icon-pack="fa"
          icon="skull-crossbones"
          type="is-danger"
          class="mr-2"
        ></b-icon>

        <span class="has-text-weight-medium has-text-danger">{{
          $t("mp:application.waiting_room.label.failed")
        }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import QrcodeVue from "qrcode.vue";
import { mapActions, mapGetters } from "vuex";
import { FLOW_POSITION } from "@/utils/const";
import { nextStepConfigOverride } from "@/utils/util";

export default {
  name: "ApplicationFlowWaitingRoom",
  components: {
    QrcodeVue,
  },
  props: {
    position: {
      type: String,
      required: true,
    },
  },
  data() {
    return {};
  },
  computed: {
    isKyc() {
      return this.position.includes("KYC");
    },
    isStarted() {
      if (this.isKyc) {
        return (
          this.status === "KYC_STARTED" ||
          this.status === "KYC_PENDING" ||
          this.status === "KYC_REVIEWING" ||
          this.status === "KYC_COMPLETED"
        );
      } else {
        return (
          this.status === "DOC_SIGN_STARTED" ||
          this.status === "DOC_SIGN_PENDING" ||
          this.status === "DOC_SIGN_REVIEWING" ||
          this.status === "DOC_SIGN_COMPLETED"
        );
      }
    },
    isDone() {
      if (this.isKyc) {
        return (
          this.status === "KYC_REVIEWING" || this.status === "KYC_COMPLETED"
        );
      } else {
        return (
          this.status === "DOC_SIGN_REVIEWING" ||
          this.status === "DOC_SIGN_COMPLETED"
        );
      }
    },
    isReviewed() {
      if (this.isKyc) {
        return (
          this.status === "KYC_COMPLETED" || this.status === "CONTRACT_CHECK"
        );
      } else {
        return (
          this.status === "DOC_SIGN_COMPLETED" ||
          this.status === "CONTRACT_SIGNED"
        );
      }
    },
    isRetry() {
      return this.result === "RETRY";
    },
    isFailed() {
      return this.result === "FAILURE";
    },
    ...mapGetters({
      mobileKyc: "application/isKycMobile",
      mobileSign: "application/isKycMobile",
      url: "application/comnicaUrl",
      status: "application/applicationStatus",
      result: "application/applicationStatusResult",
    }),
  },
  methods: {
    ...mapActions({
      savePosition: "application/savePosition",
    }),
    gotoNext() {
      let flowConfig = this.$store.getters["application/flowConfig"];
      if (this.isKyc) {
        this.savePosition({
          flowPosition: nextStepConfigOverride(
            flowConfig,
            FLOW_POSITION.KYC_PREPARATION
          ),
        });
      } else {
        this.savePosition({
          flowPosition: FLOW_POSITION.RESULT,
        });
      }
    },
  },
};
</script>

<style scoped lang="scss">
.mp-tile {
  margin: 1rem 0;
  background: #f3f5f9;
  border-radius: 8px;
  text-align: center;
  padding: 24px;
}

.required-documents {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  grid-gap: 1rem;

  .required-document-tile {
    background: #f3f5f9;
    border-radius: 8px;
    text-align: center;
    padding: 10px 0;
  }
}

.qrcode {
  display: none;
}

@media only screen and (min-width: 769px) {
  .required-documents {
    grid-template-columns: repeat(2, 1fr);
  }

  .qrcode {
    display: block;
  }
}
</style>
